<template>
    <div class="event-date-time-picker">
        <!-- Date Selection -->
        <div class="date-section">
            <date-picker v-model="selectedDate" value-type="date" format="dddd, MMMM DD, YYYY"
                :disabled-date="disabledDate" placeholder="Select date" class="date-picker" @change="onDateChange"
                :editable="false"></date-picker>
        </div>

        <!-- Time Range Selection -->
        <div class="time-section">
            <div class="time-row">
                <!-- Start Time -->
                <div class="time-input">
                    <date-picker v-model="startTime" type="time" format="HH:mm" value-type="format" placeholder="00:00"
                        class="time-picker" @change="onStartTimeChange" :editable="false"></date-picker>
                </div>

                <div class="time-separator">
                    <span>–</span>
                </div>

                <!-- End Time -->
                <div class="time-input">
                    <date-picker v-model="endTime" type="time" format="HH:mm" value-type="format" placeholder="00:00"
                        class="time-picker" :disabled-time="disabledEndTime" @change="onEndTimeChange"
                        :editable="false"></date-picker>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { eventBus } from "@/main";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';

export default {
    name: "event-date-time-picker",
    components: { DatePicker },
    props: {
        start: {
            type: String,
            default: "",
        },
        end: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            selectedDate: null,
            startTime: null,
            endTime: null,
        };
    },
    created() {
        this.initializeDates();
    },
    mounted() {
    },
    watch: {
        start: {
            handler() {
                this.initializeDates();
            },
            immediate: false
        },
        end: {
            handler() {
                this.initializeDates();
            },
            immediate: false
        }
    },
    methods: {
        initializeDates() {
            // Initialize with current date if no props provided
            const now = new Date();

            if (this.start) {
                const startDate = new Date(this.start);
                this.selectedDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

                // Check if the start time is meaningful (not midnight) or if it's from calendar selection
                if (startDate.getHours() === 0 && startDate.getMinutes() === 0) {
                    // This is likely from calendar date selection, set reasonable default time
                    const defaultStart = new Date();
                    defaultStart.setHours(defaultStart.getHours() + 1);
                    defaultStart.setMinutes(0);
                    defaultStart.setSeconds(0);
                    defaultStart.setMilliseconds(0);
                    this.startTime = `${defaultStart.getHours().toString().padStart(2, '0')}:${defaultStart.getMinutes().toString().padStart(2, '0')}`;
                } else {
                    // Use the provided start time
                    this.startTime = `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`;
                }
            } else {
                // Use today's date
                this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                // Set default start time to next hour
                const nextHour = new Date();
                nextHour.setHours(nextHour.getHours() + 1);
                nextHour.setMinutes(0);
                nextHour.setSeconds(0);
                nextHour.setMilliseconds(0);
                this.startTime = `${nextHour.getHours().toString().padStart(2, '0')}:${nextHour.getMinutes().toString().padStart(2, '0')}`;
            }

            if (this.end) {
                const endDate = new Date(this.end);
                // Check if the end time is meaningful or if we should calculate it
                if (endDate.getHours() === 0 && endDate.getMinutes() === 0) {
                    // Set end time to start time + 30 minutes
                    this.setEndTimeFromStart();
                } else {
                    this.endTime = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
                }
            } else {
                // Set end time to start time + 30 minutes
                this.setEndTimeFromStart();
            }

            // Emit the initial date-time values after initialization
            this.$nextTick(() => {
                this.emitDateTimeChange();
            });
        },

        onDateChange() {
            this.emitDateTimeChange();
        },

        onStartTimeChange(startTime) {
            if (startTime) {
                this.setEndTimeFromStart();
                this.emitDateTimeChange();
            }
        },

        onEndTimeChange() {
            this.emitDateTimeChange();
        },

        setEndTimeFromStart() {
            if (this.startTime && typeof this.startTime === 'string') {
                // Parse 24-hour time string (HH:mm format) and add 30 minutes
                const timeMatch = this.startTime.match(/(\d{2}):(\d{2})/);
                if (timeMatch) {
                    let hours = parseInt(timeMatch[1]);
                    let minutes = parseInt(timeMatch[2]);

                    // Add 30 minutes
                    minutes += 30;
                    if (minutes >= 60) {
                        hours += 1;
                        minutes -= 60;
                    }

                    // Handle 24-hour overflow
                    if (hours >= 24) {
                        hours = 0;
                    }

                    // Format back to HH:mm
                    this.endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                }
            }
        },

        disabledDate(date) {
            // Disable past dates (excluding today)
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date < today;
        },

        disabledEndTime(time) {
            if (!this.startTime || !this.selectedDate) return false;

            // Parse start time (HH:mm format)
            const startTimeMatch = this.startTime.match(/(\d{2}):(\d{2})/);
            if (!startTimeMatch) return false;

            const startHours = parseInt(startTimeMatch[1]);
            const startMinutes = parseInt(startTimeMatch[2]);

            // Handle different time input formats from the picker
            let timeHours, timeMinutes;

            if (time instanceof Date) {
                timeHours = time.getHours();
                timeMinutes = time.getMinutes();
            } else if (typeof time === 'string') {
                const timeToCheckMatch = time.match(/(\d{1,2}):(\d{2})/);
                if (!timeToCheckMatch) return false;
                timeHours = parseInt(timeToCheckMatch[1]);
                timeMinutes = parseInt(timeToCheckMatch[2]);
            } else {
                return false;
            }

            // Convert times to total minutes for easier comparison
            const startTotalMinutes = startHours * 60 + startMinutes;
            const endTotalMinutes = timeHours * 60 + timeMinutes;

            // Disable times that are before or equal to start time
            return endTotalMinutes <= startTotalMinutes;
        },

        emitDateTimeChange() {
            if (this.selectedDate && this.startTime && this.endTime) {
                // Create base date from selected date, ensuring we use the selected date properly
                const selectedDateObj = new Date(this.selectedDate);
                const year = selectedDateObj.getFullYear();
                const month = selectedDateObj.getMonth();
                const day = selectedDateObj.getDate();

                // Handle start time
                let startDateTime = new Date(year, month, day);
                if (typeof this.startTime === 'string') {
                    // Parse 24-hour time string (HH:mm format)
                    const timeMatch = this.startTime.match(/(\d{2}):(\d{2})/);
                    if (timeMatch) {
                        const hours = parseInt(timeMatch[1]);
                        const minutes = parseInt(timeMatch[2]);
                        startDateTime.setHours(hours);
                        startDateTime.setMinutes(minutes);
                    }
                }

                // Handle end time
                let endDateTime = new Date(year, month, day);
                if (typeof this.endTime === 'string') {
                    // Parse 24-hour time string (HH:mm format)
                    const timeMatch = this.endTime.match(/(\d{2}):(\d{2})/);
                    if (timeMatch) {
                        const hours = parseInt(timeMatch[1]);
                        const minutes = parseInt(timeMatch[2]);
                        endDateTime.setHours(hours);
                        endDateTime.setMinutes(minutes);
                    }
                }

                // Set seconds and milliseconds to 0
                startDateTime.setSeconds(0);
                startDateTime.setMilliseconds(0);
                endDateTime.setSeconds(0);
                endDateTime.setMilliseconds(0);


                // Emit the combined date-time values using moment ISO strings
                this.$emit('date-time-change', {
                    start: moment(startDateTime).toISOString(),
                    end: moment(endDateTime).toISOString(),
                    date: moment(this.selectedDate).toISOString()
                });
            }
        }
    },
};
</script>

<style lang="scss" scoped>
.event-date-time-picker {
    display: flex;
    gap: 5px;
    border-radius: 12px;
    align-items: flex-start;
    width: 100%;
    max-width: 100%;
}

.date-section {
    display: flex;
    flex-direction: column;
    width: calc(100% - 195px);
}

.time-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 3;
    min-width: 0;
}

.time-row {
    display: flex;
    align-items: center;
    width: 100%;
}

.time-input {
    width: max-content;
}

.time-separator {
    font-size: 12px;
    color: #888;
    margin: 0 2px;
}

.picker-label {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
}

.date-picker {
    width: 100%;

    :deep(.mx-input) {
        border: 1px solid #e4e4e4;
        border-radius: 4px;
        padding: 12px 16px;
        font-size: 12px;
        width: 100%;
        box-sizing: border-box;

        &:focus {
            border-color: #007bff;
            outline: none;
        }

        &::placeholder {
            color: #888;
        }
    }

    :deep(.mx-icon-calendar) {
        color: #888;
    }
}

.time-picker {
    width: 90px;

    :deep(.mx-input) {
        border: 1px solid #e4e4e4;
        border-radius: 4px;
        padding: 12px;
        font-size: 12px;
        width: 100%;
        box-sizing: border-box;

        &:focus {
            border-color: #007bff;
            outline: none;
        }

        &::placeholder {
            color: #888;
        }
    }

    :deep(.mx-icon-time) {
        color: #888;
    }
}
</style>

<style lang="scss">
.mx-datepicker-popup {
    z-index: 999999999 !important;
    background: #ffffff !important;
    border: 1px solid #e4e4e4 !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;

    .mx-calendar {
        background: #ffffff !important;
        color: #333 !important;
    }

    .mx-calendar-header {
        background: #ffffff !important;
        color: #333 !important;
        border-bottom: 1px solid #e4e4e4 !important;
    }

    .mx-btn {
        color: #333 !important;

        &:hover {
            background: #f5f5f5 !important;
        }
    }

    .mx-calendar-content {
        .mx-date {
            color: #333 !important;

            &:hover {
                background: #f5f5f5 !important;
            }

            &.active {
                background: #007bff !important;
                color: white !important;
            }

            &.disabled {
                color: #ccc !important;
            }
        }
    }

    .mx-time-picker {
        background: #ffffff !important;

        .mx-time-column {
            background: #ffffff !important;

            .mx-time-item {
                color: #333 !important;

                &:hover {
                    background: #f5f5f5 !important;
                }

                &.active {
                    background: #007bff !important;
                    color: white !important;
                }

                &.disabled {
                    color: #ccc !important;
                    cursor: not-allowed !important;
                }
            }
        }
    }
}
</style>
